import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  DarkMode as DarkModeIcon,
  VolumeUp,
  VolumeOff,
} from "@mui/icons-material";
import { useThemeToggle } from "../context/ThemeContext";

const Settings = () => {
  const { mode, toggleTheme } = useThemeToggle();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Handle sound notifications toggle
  const handleSoundToggle = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);
    
    // Save to localStorage
    localStorage.setItem('notificationSoundEnabled', newSoundEnabled.toString());
    
    if (newSoundEnabled) {
      showSnackbar("Notification sounds enabled", "success");
    } else {
      showSnackbar("Notification sounds disabled", "info");
    }
  };

  // Load sound preference from localStorage on component mount
  useEffect(() => {
    const savedSoundPreference = localStorage.getItem('notificationSoundEnabled');
    if (savedSoundPreference !== null) {
      setSoundEnabled(savedSoundPreference === 'true');
    }
  }, []);

  // Snackbar functions
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const settingsOptions = [
    {
      icon: <PersonIcon sx={{ color: mode === "dark" ? "#F1F5F9" : "#050712" }} />,
      primary: "Profile Settings",
      secondary: "Update your personal information",
    },
    {
      icon: soundEnabled ? 
        <VolumeUp sx={{ color: mode === "dark" ? "#F1F5F9" : "#050712" }} /> : 
        <VolumeOff sx={{ color: mode === "dark" ? "#F1F5F9" : "#050712" }} />,
      primary: "Notification Sounds",
      secondary: soundEnabled ? "Sounds enabled for notifications" : "Sounds disabled for notifications",
      hasSwitch: true,
      switchAction: handleSoundToggle,
      switchChecked: soundEnabled,
    },
    
    {
      icon: <SecurityIcon sx={{ color: mode === "dark" ? "#F1F5F9" : "#050712" }} />,
      primary: "Security",
      secondary: "Change password and security settings",
    },
    {
      icon: <SettingsIcon sx={{ color: mode === "dark" ? "#F1F5F9" : "#050712" }} />,
      primary: "General",
      secondary: "General application settings",
    },
    {
      icon: <DarkModeIcon sx={{ color: mode === "dark" ? "#F1F5F9" : "#050712" }} />,
      primary: "Dark Mode",
      secondary: "Switch between light and dark theme",
      hasSwitch: true,
      switchAction: toggleTheme,
      switchChecked: mode === "dark",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: 4,
        px: 2,
        mt:-4,
        ml:-10,
      }}
    >
      <Container maxWidth="md">
     
        {/* Settings Card */}
        <Paper
          elevation={mode === "dark" ? 0 : 2}
          sx={{
            background: mode === "dark" 
              ? "linear-gradient(135deg, #1E293B 0%, #334155 100%)" 
              : "#FFFFFF",
            borderRadius: "12px",
            border: mode === "dark" 
              ? "1px solid rgba(255,255,255,0.1)" 
              : "1px solid #E2E8F0",
            overflow: "hidden",
            boxShadow: mode === "dark" 
              ? "0 4px 20px rgba(0, 0, 0, 0.3)" 
              : "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <List sx={{ py: 0 }}>
            {settingsOptions.map((option, index) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    py: 2.5,
                    px: 3,
                    "&:hover": {
                      background: mode === "dark" 
                        ? "rgba(255,255,255,0.05)" 
                        : "rgba(0, 0, 0, 0.02)",
                      transition: "all 0.2s ease-in-out",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 48 }}>
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={option.primary}
                    secondary={option.secondary}
                    primaryTypographyProps={{
                      sx: { 
                        color: mode === "dark" ? "#F1F5F9" : "#1E293B",
                        fontWeight: 600,
                        fontSize: "1rem"
                      },
                    }}
                    secondaryTypographyProps={{
                      sx: { 
                        color: mode === "dark" ? "#94A3B8" : "#64748B",
                        fontSize: "0.875rem"
                      },
                    }}
                  />
                  {option.hasSwitch && (
                    <Switch
                      checked={option.switchChecked ?? false}
                      onChange={option.switchAction ?? (() => {})}
                      sx={{
                        "& .MuiSwitch-switchBase": {
                          "&.Mui-checked": {
                            color: "#60A5FA",
                            "& + .MuiSwitch-track": {
                              backgroundColor: "#60A5FA",
                            },
                          },
                        },
                        "& .MuiSwitch-thumb": {
                          backgroundColor: mode === "dark" ? "#E2E8F0" : "#FFFFFF",
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: mode === "dark" ? "#475569" : "#CBD5E1",
                          opacity: 1,
                        },
                      }}
                    />
                  )}
                </ListItem>

                {index < settingsOptions.length - 1 && (
                  <Divider sx={{ 
                    borderColor: mode === "dark" 
                      ? "rgba(255,255,255,0.1)" 
                      : "rgba(0, 0, 0, 0.08)" 
                  }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </Paper>

       

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Settings;